/** Préférences utilisateur de gestion (managementPreferences) — helpers purs, sans dépendance
 *  runtime (ni auth, ni React). Isolés ici pour être consommables depuis des modules bas-niveau
 *  (ex: résolution de route au login) sans créer de cycle d'import via `useMyProfile`.
 *
 *  Contrat API : dataMyProfile.managementPreferences = [{ feature, category, version, payload }].
 *  `payload` (JSON libre, optionnel) porte une configuration structurée — ex: layout du dashboard
 *  admin ({ order: string[], hidden: string[] }) ou page de démarrage ({ route: string | null }). */
export type ManagementPreference = {
  feature: string
  category?: string | null
  version: number
  payload?: unknown
}

/**
 * Renvoie la version choisie par l'utilisateur pour une feature de gestion
 * (`managementPreferences`), ou `defaultVersion` si aucune preference n'est enregistree.
 * Tolerant : dataMyProfile est `any` et `managementPreferences` peut etre absent.
 */
export const getManagementFeatureVersion = (
  dataMyProfile: any,
  feature: string,
  defaultVersion = 1
): number => {
  const prefs = dataMyProfile?.managementPreferences as ManagementPreference[] | undefined
  if (!Array.isArray(prefs)) return defaultVersion
  const match = prefs.find(p => p?.feature === feature)
  return typeof match?.version === 'number' ? match.version : defaultVersion
}

/**
 * Renvoie le `payload` JSON d'une feature de gestion (`managementPreferences`), ou `null` si la
 * preference est absente ou sans payload. Miroir tolerant de {@link getManagementFeatureVersion} :
 * dataMyProfile est `any`, `managementPreferences` peut manquer, `payload` peut etre absent.
 */
export const getManagementFeaturePayload = <T = unknown,>(
  dataMyProfile: any,
  feature: string
): T | null => {
  const prefs = dataMyProfile?.managementPreferences as ManagementPreference[] | undefined
  if (!Array.isArray(prefs)) return null
  const match = prefs.find(p => p?.feature === feature)
  return (match?.payload as T | undefined) ?? null
}
